import axios, { AxiosInstance } from 'axios';
import { format, isValid, parseISO } from 'date-fns';
import { apiConfig } from '@/config/apiConfig';
import { getVoiceFallbackClient, mapConnectionToIntegrationRow } from './_shared';

// ── Voice integrations (/voice-integrations) ──────────────────────

export type VoiceIntegrationRow = {
  id: string;
  name: string;
  desc: string;
  icon: string;
  connected: boolean;
  category: string;
  fields: string[];
  /** Present when connected — required for `DELETE /api/integrations/{connection_id}` and test. */
  connectionId?: string;
};

const INITIAL_VOICE_INTEGRATIONS: VoiceIntegrationRow[] = [
  { id: 'hubspot', name: 'HubSpot', desc: 'CRM · Contacts, Deals, Properties', icon: 'solar:database-bold', connected: false, category: 'crm', fields: [] },
  { id: 'calendly', name: 'Calendly', desc: 'Calendar · Real-time slot booking', icon: 'solar:calendar-date-bold', connected: false, category: 'calendar', fields: ['Calendly personal access token', 'Default event type URI'] },
  { id: 'calcom', name: 'Cal.com', desc: 'Calendar · Scheduling with Cal.com', icon: 'solar:calendar-bold', connected: false, category: 'calendar', fields: ['Cal.com API key', 'Event type URL or ID', 'Default timezone'] },
  { id: 'salesforce', name: 'Salesforce', desc: 'CRM · Leads, Opportunities, Campaigns', icon: 'solar:cloud-bold', connected: false, category: 'crm', fields: ['Instance URL', 'Connected App — Client ID', 'Connected App — Client Secret', 'API version', 'Sync direction'] },
  { id: 'slack', name: 'Slack', desc: 'Messaging · Escalation alerts & notifications', icon: 'solar:chat-round-dots-bold', connected: false, category: 'messaging', fields: ['Incoming webhook URL', 'Default channel'] },
  { id: 'zendesk', name: 'Zendesk', desc: 'Support · Ticket creation from calls', icon: 'solar:ticket-bold', connected: false, category: 'support', fields: ['Subdomain', 'Agent email', 'API token'] },
  { id: 'webhook', name: 'Custom Webhook', desc: 'API · POST session data to any endpoint', icon: 'solar:link-bold', connected: false, category: 'custom', fields: ['Endpoint URL', 'Authorization header value (optional)', 'Payload format', 'Retry count'] },
  { id: 'twilio', name: 'Twilio', desc: 'Voice & SMS · Call routing & messaging', icon: 'solar:phone-bold', connected: false, category: 'voice', fields: ['Account SID', 'Auth Token'] },
  { id: 'sendgrid', name: 'SendGrid', desc: 'Email · Transactional & booking confirmations', icon: 'solar:letter-bold', connected: false, category: 'email', fields: ['SendGrid API key', 'Default from address', 'Default from name'] },
  { id: 'zapier', name: 'Zapier', desc: 'Automation · Connect to 5,000+ tools', icon: 'solar:bolt-bold', connected: false, category: 'automation', fields: ['Zapier "Catch Hook" URL'] },
  { id: 'klaviyo', name: 'Klaviyo', desc: 'Marketing · Email & SMS automation', icon: 'solar:letter-bold', connected: false, category: 'marketing', fields: ['Klaviyo Private API Key'] },
  { id: 'gohighlevel', name: 'GoHighLevel', desc: 'CRM · All-in-one marketing platform', icon: 'solar:rocket-bold', connected: false, category: 'crm', fields: ['Agency API key', 'Location ID'] },
];

function cloneVoiceIntegration(i: VoiceIntegrationRow): VoiceIntegrationRow {
  return { ...i, fields: [...i.fields] };
}

let voiceIntegrationsStore: VoiceIntegrationRow[] = INITIAL_VOICE_INTEGRATIONS.map(cloneVoiceIntegration);

function mapCatalogIntegrationItem(item: any, connected: boolean, connectionId?: string): VoiceIntegrationRow {
  const intId = String(item.id ?? item.integration_id ?? '');
  const fallback = INITIAL_VOICE_INTEGRATIONS.find(i => i.id === intId);
  return {
    id: intId,
    name: String(item.name ?? item.title ?? fallback?.name ?? 'Integration'),
    desc: String(item.desc ?? item.description ?? fallback?.desc ?? ''),
    icon: String(item.icon ?? fallback?.icon ?? 'solar:plug-circle-bold'),
    connected,
    category: String(item.category ?? fallback?.category ?? 'integration'),
    fields: (Array.isArray(item.fields) && item.fields.length > 0) ? item.fields.map(String) : (fallback?.fields || []),
    connectionId: connectionId ? String(connectionId) : undefined,
  };
}

function normalizeIntegrationsResponse(data: unknown): VoiceIntegrationRow[] {
  if (!data) return [];
  if (Array.isArray(data)) {
    return data.map((item: any) => {
      const hasConn = Boolean(item?.integration_id && item?.workspace_id);
      return mapCatalogIntegrationItem(item, hasConn || item?.status === 'active', hasConn ? item.id : undefined);
    });
  }
  if (typeof data === 'object') {
    const o = data as Record<string, unknown>;
    const catalog = Array.isArray(o.catalog) ? o.catalog : [];
    const connected = Array.isArray(o.connected) ? o.connected : [];
    const connByInt = new Map<string, any>();
    for (const c of connected) {
      if (c && typeof c === 'object' && (c as any).integration_id != null) {
        connByInt.set(String((c as any).integration_id), c);
      }
    }
    const rows: VoiceIntegrationRow[] = (catalog as any[]).map((item) => {
      const intId = String(item.integration_id ?? item.id ?? '');
      const conn = connByInt.get(intId);
      return mapCatalogIntegrationItem(item, Boolean(conn), conn?.id);
    });
    const seen = new Set(rows.map((r) => r.id));
    for (const c of connected as any[]) {
      const intId = String(c.integration_id ?? '');
      if (intId && !seen.has(intId)) {
        rows.push(mapConnectionToIntegrationRow(c));
        seen.add(intId);
      }
    }
    return rows;
  }
  return [];
}

export async function fetchVoiceIntegrations(axiosInstance?: AxiosInstance): Promise<VoiceIntegrationRow[]> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = apiConfig.voiceEndpoints.integrations;
  const response = await client.get<unknown>(url);
  return normalizeIntegrationsResponse(response.data);
}

export async function connectVoiceIntegration(
  integrationId: string,
  axiosInstance?: AxiosInstance,
  payload?: { config?: Record<string, any>; credentials?: Record<string, any> }
): Promise<VoiceIntegrationRow | null> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.integrations}/connect`;
  const response = await client.post<any>(url, {
    integration_id: integrationId,
    name: null,
    config: payload?.config || {},
    credentials: payload?.credentials || {},
  });
  const rows = await fetchVoiceIntegrations(axiosInstance);
  return rows.find((r) => r.id === integrationId) ?? mapConnectionToIntegrationRow(response.data);
}

/** Initiate HubSpot OAuth for the voice agent's CRM tools — returns { auth_url } to redirect to.
 * No API key/Portal ID collected here; HubSpot's own login handles authentication. */
export async function initHubSpotVoiceOAuth(axiosInstance?: AxiosInstance): Promise<{ auth_url: string }> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.integrations}/hubspot/oauth/init`;
  const response = await client.post<{ auth_url: string }>(url);
  return response.data;
}

export async function disconnectVoiceIntegration(connectionId: string, axiosInstance?: AxiosInstance): Promise<boolean> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.integrations}/${connectionId}`;
  const response = await client.delete(url);
  return response.status === 204 || response.status === 200;
}

export async function testVoiceIntegrationConnection(connectionId: string, axiosInstance?: AxiosInstance): Promise<{ ok: boolean; message: string }> {
  if (!connectionId || connectionId === 'webhook') {
    return { ok: false, message: 'No integration connection to test.' };
  }
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.integrations}/${connectionId}/test`;
  const response = await client.post<{ ok: boolean; message: string }>(url);
  return response.data;
}

/** OpenAPI has no custom-webhook path; acknowledge save client-side without calling a bogus URL. */
function catalogEntriesFromPayload(data: unknown): any[] {
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object') {
    const o = data as Record<string, unknown>;
    const cand = o.items ?? o.integrations ?? o.results ?? o.catalog;
    if (Array.isArray(cand)) return cand;
  }
  return [];
}

/** Prefer a catalog row that looks like a webhook / outbound HTTP integration. */
function pickWebhookIntegrationId(catalogData: unknown): string | null {
  const list = catalogEntriesFromPayload(catalogData);
  for (const item of list) {
    const id = item?.integration_id ?? item?.slug ?? item?.id;
    if (!id) continue;
    const hay = JSON.stringify(item).toLowerCase();
    if (/webhook|custom url|http callback|outbound/.test(hay)) return String(id);
  }
  for (const item of list) {
    const id = item?.integration_id ?? item?.id;
    if (id) return String(id);
  }
  return null;
}

/**
 * Persist custom webhook via `POST /api/integrations/connect` when the Voice catalog exposes a matching integration.
 */
export async function saveVoiceCustomWebhook(
  input: { endpointUrl?: string; authHeader?: string },
  axiosInstance?: AxiosInstance
): Promise<{ ok: boolean; message?: string }> {
  const client = axiosInstance || getVoiceFallbackClient();
  const endpointUrl = input.endpointUrl?.trim();
  if (!endpointUrl) return { ok: false, message: 'Endpoint URL is required.' };
  try {
    const catRes = await client.get<unknown>(`${apiConfig.voiceEndpoints.integrations}/catalog`);
    const integration_id = pickWebhookIntegrationId(catRes.data);
    if (!integration_id) {
      return { ok: false, message: 'No integration in catalog could be mapped for this webhook save.' };
    }
    await client.post(`${apiConfig.voiceEndpoints.integrations}/connect`, {
      integration_id,
      name: 'Custom Webhook',
      config: {
        endpoint_url: endpointUrl,
        auth_header: input.authHeader?.trim() || undefined,
      },
      credentials: {},
    });
    return { ok: true };
  } catch (e: any) {
    const detail = e?.response?.data?.detail ?? e?.response?.data?.message ?? e?.message;
    return { ok: false, message: typeof detail === 'string' ? detail : JSON.stringify(detail ?? 'Save failed') };
  }
}

// ── Integration extras ────────────────────────────────────────────────

/** `PUT /api/integrations/{connection_id}` — update an existing connection config. */
export async function updateVoiceIntegration(connectionId: string, patch: { name?: string; config?: Record<string, unknown>; credentials?: Record<string, unknown> }, axiosInstance?: AxiosInstance): Promise<VoiceIntegrationRow | null> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.put<any>(`${apiConfig.voiceEndpoints.integrations}/${connectionId}`, patch);
  return response.data ? mapConnectionToIntegrationRow(response.data) : null;
}

export type CalcomEventType = { id: number; title: string; slug: string; length: number };

/** `POST /api/integrations/calcom/event-types` — list Cal.com event types for a given API key. */
export async function fetchCalcomEventTypes(apiKey: string, axiosInstance?: AxiosInstance): Promise<CalcomEventType[]> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.post<any>(`${apiConfig.voiceEndpoints.integrations}/calcom/event-types`, { api_key: apiKey });
  const list = Array.isArray(response.data) ? response.data : response.data?.event_types ?? [];
  return list.map((e: any) => ({ id: Number(e.id), title: String(e.title ?? e.name ?? ''), slug: String(e.slug ?? ''), length: Number(e.length ?? 0) }));
}
